import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { testIds } from "@/testIds";
import { useAppCrumbItem } from "@/components/navigation/AppCrumb";
import {
  addPreambleLines,
  clampSplit,
  figureColoursUrl,
  layoutForWidth,
  missingPreambleLines,
  parseFigureColours,
  preambleNotice,
  readSplit,
  removePreambleLines,
  statusText,
  drawTime,
  writeSplit,
  type FigureColours,
  type SplitLayout,
} from "@/lib/tikzAuthoring";
import { isOurSide } from "@/lib/tikzFigure";
import {
  TIKZ_FIGURE_ACTION_EVENT,
  addedRanges,
  clearFigureScope,
  clearPendingChange,
  lineDiff,
  publishFigure,
  setFigureScope,
  useTikzAsk,
  type FigureActionDetail,
} from "@/lib/tikzAsk";
import type { TikzTemplate } from "@/lib/tikzTemplates";
import { ourSideWording } from "./TikzFigure";
import { TikzSourceEditor, type TikzSourceEditorHandle } from "./TikzSourceEditor";
import { TikzTemplates } from "./TikzTemplates";
import { TikzBeforeAfter, TikzDiffBar, TikzDiffSource } from "./TikzAskDiff";
import askStyles from "./TikzAskDiff.module.scss";
import { TOO_LONG_MESSAGE, useTikzDraft } from "./useTikzDraft";
import type { PreambleAccess } from "./useDocumentPreamble";
import type { InlineErrorSpec } from "./tikzCodeMirror";
import styles from "./TikzSplitView.module.scss";
import type { TableData } from "@/components/editor/table-grid/tableModel";
import { isPlotProblem, legendCornerOf, plotSource, type PlotSpec } from "@/lib/tikzPlot";
import { PlotSettings } from "../../plot/PlotSettings";

const T = testIds.tikzSplit;

type Fetch = (url: string, init?: RequestInit) => Promise<Response>;

const ZOOMS = [
  { value: "fit", label: "Fit" },
  { value: "0.5", label: "50 %" },
  { value: "1", label: "100 %" },
  { value: "1.5", label: "150 %" },
  { value: "2", label: "200 %" },
];

const FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

/** The width the sheet has to work with: its host, the workspace (the window when it can't be measured). */
function useHostWidth(el: HTMLElement | null): number {
  const measure = useCallback(() => {
    const host = el?.parentElement ?? null;
    const w = host?.getBoundingClientRect().width ?? 0;
    return w > 0 ? w : window.innerWidth;
  }, [el]);
  const [width, setWidth] = useState(() => (typeof window === "undefined" ? 1280 : window.innerWidth));
  useEffect(() => {
    setWidth(measure());
    const onResize = () => setWidth(measure());
    window.addEventListener("resize", onResize);
    let ro: ResizeObserver | null = null;
    if (el?.parentElement && typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(onResize);
      ro.observe(el.parentElement);
    }
    return () => {
      window.removeEventListener("resize", onResize);
      ro?.disconnect();
    };
  }, [el, measure]);
  return width;
}

export interface TikzSplitViewProps {
  fetchWithAuth: Fetch;
  documentId: string;
  blockId: string;
  source: string;
  caption: string;
  label: string;
  /** The canvas's drawing, shown until the first draft answers. */
  initialSvg: string | null;
  /** "Figure 3", or "Figure" for a bare picture: Ask Lilia's context chip (1c). */
  figureName?: string;
  preamble: PreambleAccess;
  /** No workspace host was found: the sheet covers the window. */
  fixed?: boolean;
  onSourceChange: (next: string) => void;
  onCaptionChange: (next: string) => void;
  /** The caption field lost focus: derive a label, as the canvas does. */
  onCaptionCommit: () => void;
  /** Each drawing that drew, for the canvas behind. */
  onDrawn?: (good: { hash: string; svg: string }) => void;
  onDone: () => void;
  /** A plot linked to a table (step 4a, 1c): a Plot tab beside Source, and detaching. */
  plot?: SplitPlot;
}

export interface SplitPlot {
  /** The table as it is now; null when it was removed (the settings are read-only). */
  table: TableData | null;
  spec: PlotSpec;
  /** "Table 2", "a table". */
  tableName: string;
  /** Why it can't be drawn from the table now, in words. */
  problem: string | null;
  /** Series dropped because their column left the table. */
  dropped: string[];
  /** Plot settings opens the Plot tab; Edit source opens Source. */
  initialView?: "plot" | "source";
  onSpecChange: (spec: PlotSpec) => void;
  onDetach: () => void;
}

/**
 * The split view (TikZ step 3, 1a): a sheet over the workspace — the top bar
 * and the Ask Lilia dock stay — with the source beside its drawing. The
 * source autosaves like any block (each change goes to the figure node, so it
 * joins the document's undo); the drawing is the draft route's, redrawn 600 ms
 * after typing stops. Done or Esc goes back to the figure, selected.
 */
export function TikzSplitView(props: TikzSplitViewProps) {
  const { fetchWithAuth, documentId, blockId, source, preamble } = props;
  useAppCrumbItem("Figure");
  const [root, setRoot] = useState<HTMLDivElement | null>(null);
  const width = useHostWidth(root);
  const layout: SplitLayout = layoutForWidth(width);
  const [tab, setTab] = useState<"source" | "drawing">("source");
  const [split, setSplit] = useState(readSplit);
  const [zoom, setZoom] = useState("fit");
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);
  const [escArmed, setEscArmed] = useState(false);
  const [colours, setColours] = useState<FigureColours>({});
  const [showGallery, setShowGallery] = useState(() => !source.trim());
  // Source that arrives from outside (Ask Lilia's drawing, inserted) closes the empty state.
  const hasSource = !!source.trim();
  useEffect(() => {
    if (hasSource) setShowGallery(false);
  }, [hasSource]);
  const [popover, setPopover] = useState(false);
  const [confirm, setConfirm] = useState<TikzTemplate | null>(null);
  const [notice, setNotice] = useState<{ text: string; lines: string[] } | null>(null);
  const editor = useRef<TikzSourceEditorHandle>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  const draft = useTikzDraft({ fetchWithAuth, documentId, blockId, source, initialSvg: props.initialSvg });

  // A linked plot (step 4a): Plot · Source in the left pane; editing the source asks once.
  const plot = props.plot;
  const [leftView, setLeftView] = useState<"plot" | "source">(plot ? (plot.initialView ?? "plot") : "source");
  useEffect(() => {
    if (!plot) setLeftView("source");
  }, [plot]);
  const [detachAsk, setDetachAsk] = useState<null | { apply: (() => void) | null }>(null);
  const afterDetach = useRef<(() => void) | null>(null);
  useEffect(() => {
    // Detached: the plot is gone from the props, the editor unlocked; the held keystroke goes in.
    if (!plot && afterDetach.current) {
      const apply = afterDetach.current;
      afterDetach.current = null;
      apply();
      requestAnimationFrame(() => editor.current?.focus());
    }
  }, [plot]);
  const detach = (apply: (() => void) | null) => {
    setDetachAsk(null);
    afterDetach.current = apply ?? (() => {});
    plot?.onDetach();
  };
  const changePlot = (next: PlotSpec, immediate: boolean) => {
    plot?.onSpecChange(next);
    if (!immediate || !plot?.table) return;
    const src = plotSource(plot.table, next, plot.tableName);
    if (!isPlotProblem(src)) draft.drawNow(src);
  };
  const { onDrawn } = props;
  useEffect(() => {
    if (draft.good) onDrawn?.(draft.good);
  }, [draft.good, onDrawn]);

  // Ask Lilia on this figure (1c) ------------------------------------------------------
  //
  // While the split view is open the one dock is scoped to this figure, and
  // reads its state from here (the draft's, newer than the canvas's).
  const figureName = props.figureName ?? "Figure";
  useEffect(() => {
    setFigureScope({ documentId, blockId, afterBlockId: null, section: null, owner: "split" });
    return () => clearFigureScope("split", blockId);
  }, [documentId, blockId]);
  const draftError = draft.state === "error" ? draft.error : null;
  useEffect(() => {
    publishFigure({
      documentId,
      blockId,
      name: figureName,
      source,
      caption: props.caption,
      label: props.label,
      state: !source.trim() ? "empty" : draftError ? "error" : "drawn",
      error: draftError ? { line: draftError.line, message: draftError.message } : null,
      splitOpen: true,
    });
  }, [documentId, blockId, figureName, source, props.caption, props.label, draftError]);
  const pending = useTikzAsk((st) => st.pending[blockId] ?? null);
  const highlight = useTikzAsk((st) => (st.highlight?.blockId === blockId ? st.highlight : null));
  const [showAfter, setShowAfter] = useState(true);
  useEffect(() => setShowAfter(true), [pending]);

  /** Keep: the change becomes ordinary text, one undoable step, with any library line it needs. */
  const keepChange = async (typed?: string) => {
    const change = pending;
    if (!change) return;
    clearPendingChange(blockId);
    setShowGallery(false);
    const first = addedRanges(lineDiff(source, change.source))[0]?.from ?? 1;
    if (editor.current) editor.current.applyChange(change.source, first);
    else props.onSourceChange(change.source);
    if (typed !== undefined) {
      const v = editor.current?.view();
      if (v) {
        if (typed.length === 1) v.dispatch(v.state.replaceSelection(typed), { userEvent: "input.type" });
        else if (typed === "Enter") v.dispatch(v.state.replaceSelection("\n"), { userEvent: "input.type" });
      }
    }
    requestAnimationFrame(() => editor.current?.focus());
    const lines = change.preambleAdditions.filter((l) => !preamble.read().includes(l));
    if (lines.length) {
      try {
        await preamble.write(addPreambleLines(preamble.read(), lines));
        setNotice({ text: preambleNotice(lines), lines });
      } catch {
        setNotice({ text: `Couldn't add ${lines.join(", ")} to the preamble. Add it in Document settings.`, lines: [] });
      }
    }
  };
  const discardChange = () => {
    clearPendingChange(blockId);
    requestAnimationFrame(() => editor.current?.focus());
  };

  // An Explain chip was clicked: bring its lines into view.
  useEffect(() => {
    const onAction = (e: Event) => {
      const d = (e as CustomEvent<FigureActionDetail>).detail;
      if (d?.blockId !== blockId || d.action.type !== "scroll-to-line") return;
      const line = d.action.line;
      if (pending) root?.querySelector(`[data-line="${line}"]`)?.scrollIntoView?.({ block: "center" });
      else editor.current?.scrollToLine(line);
    };
    window.addEventListener(TIKZ_FIGURE_ACTION_EVENT, onAction);
    return () => window.removeEventListener(TIKZ_FIGURE_ACTION_EVENT, onAction);
  }, [blockId, pending, root]);

  // The theme's colour names, resolved for this figure (its chapter, in Index).
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const res = await fetchWithAuth(figureColoursUrl(documentId, blockId));
        if (!res.ok) return;
        const body = await res.json();
        if (live) setColours(parseFigureColours(body));
      } catch {
        /* no swatches; the names still complete */
      }
    })();
    return () => {
      live = false;
    };
  }, [fetchWithAuth, documentId, blockId]);

  // The caption is typed here: local while focused, so the caret never jumps.
  const [caption, setCaption] = useState(props.caption);
  const captionFocused = useRef(false);
  useEffect(() => {
    if (!captionFocused.current) setCaption(props.caption);
  }, [props.caption]);

  const error = draft.state === "error" ? draft.error : null;
  const inlineError: InlineErrorSpec | null = useMemo(
    () =>
      error && error.kind === "tex" && error.line !== null
        ? {
            line: error.line,
            excerpt: error.excerpt,
            column: error.column,
            message: error.message,
            note: draft.svg ? "The drawing shows the last version that drew." : null,
          }
        : null,
    // The note follows whether a last version exists, not which one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [error, !!draft.svg],
  );

  // Templates --------------------------------------------------------------------------

  const applyTemplate = async (t: TikzTemplate) => {
    setConfirm(null);
    setPopover(false);
    setShowGallery(false);
    // Through the editor when it's there, so the old source stays in its undo.
    if (editor.current) editor.current.replaceAll(t.source);
    else props.onSourceChange(t.source);
    const before = preamble.read();
    const lines = missingPreambleLines(before, t.needs);
    if (lines.length) {
      try {
        await preamble.write(addPreambleLines(before, lines));
        setNotice({ text: preambleNotice(lines), lines });
      } catch {
        setNotice({ text: `Couldn't add ${lines.join(", ")} to the preamble. Add it in Document settings.`, lines: [] });
      }
    } else {
      setNotice(null);
    }
    draft.drawNow(t.source);
    requestAnimationFrame(() => editor.current?.focus());
  };

  const pickTemplate = (t: TikzTemplate) => {
    setPopover(false);
    if (source.trim()) setConfirm(t);
    else void applyTemplate(t);
  };

  const undoPreamble = async () => {
    const n = notice;
    setNotice(null);
    if (!n?.lines.length) return;
    try {
      await preamble.write(removePreambleLines(preamble.read(), n.lines));
    } catch {
      /* the line stays; Document settings can remove it */
    }
  };

  // Keys anywhere in the sheet --------------------------------------------------------
  //
  // A native listener, so the page's own window shortcuts don't also run while
  // the author types here: ⌘S there cuts a checkpoint, ⌘Z undoes the document
  // (it would fight the source editor's undo), ⌘D duplicates the focused block.
  const keys = useRef({ done: props.onDone, drawNow: draft.drawNow, popover, confirm, pending, keepChange, discardChange });
  keys.current = { done: props.onDone, drawNow: draft.drawNow, popover, confirm, pending, keepChange, discardChange };
  useEffect(() => {
    if (!root) return;
    const onKey = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      if (!e.defaultPrevented && keys.current.pending && mod && (e.key === "Enter" || e.key === "Backspace")) {
        // ⌘⏎ · ⌘⌫: Keep · Discard Ask Lilia's change.
        e.preventDefault();
        if (e.key === "Enter") void keys.current.keepChange();
        else keys.current.discardChange();
      } else if (!e.defaultPrevented) {
        if (e.key === "Escape") {
          e.preventDefault();
          if (keys.current.confirm) setConfirm(null);
          else if (keys.current.popover) setPopover(false);
          else keys.current.done();
        } else if (mod && e.key.toLowerCase() === "s") {
          e.preventDefault();
          keys.current.drawNow();
        }
      }
      // ⌘K (search) and ⌘\ (source pane) stay the page's.
      if ((mod && !["k", "\\"].includes(e.key.toLowerCase())) || e.key === "Escape") e.stopPropagation();
    };
    root.addEventListener("keydown", onKey);
    return () => root.removeEventListener("keydown", onKey);
  }, [root]);

  // Esc then Tab: focus goes on to whatever follows the editor in the sheet.
  const leaveEditor = () => {
    if (!root) return;
    const sourcePane = root.querySelector(`[data-testid="${T.source}"]`);
    const all = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
      (el) => !sourcePane?.contains(el) && !el.hasAttribute("disabled"),
    );
    const next =
      all.find((el) => sourcePane && sourcePane.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING) ?? all[0];
    if (next) next.focus();
    else (document.activeElement as HTMLElement | null)?.blur?.();
  };

  // The splitter ----------------------------------------------------------------------

  const dragging = useRef(false);
  const onSplitterDown = (e: React.PointerEvent<HTMLDivElement>) => {
    dragging.current = true;
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onSplitterMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging.current || !gridRef.current) return;
    const r = gridRef.current.getBoundingClientRect();
    if (r.width > 0) setSplit(clampSplit((e.clientX - r.left) / r.width));
  };
  const onSplitterUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging.current) return;
    dragging.current = false;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    setSplit((f) => {
      writeSplit(f);
      return f;
    });
  };
  const onSplitterKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const step = e.key === "ArrowLeft" ? -0.02 : e.key === "ArrowRight" ? 0.02 : 0;
    if (!step) return;
    e.preventDefault();
    const next = clampSplit(split + step);
    setSplit(next);
    writeSplit(next);
  };

  // Rendering -------------------------------------------------------------------------

  const status = statusText({
    state: draft.state,
    drawMs: draft.drawMs,
    elapsedMs: draft.elapsedMs,
    line: error?.line ?? null,
    message: error?.message === TOO_LONG_MESSAGE ? TOO_LONG_MESSAGE : null,
  });
  const proposedStatus = pending
    ? pending.drew
      ? `Proposed · drew${drawTime(pending.drawMs) ? ` in ${drawTime(pending.drawMs)}` : ""}`
      : "Proposed · didn’t draw"
    : null;
  const chip = (
    <span
      className={styles.status}
      data-testid={T.status}
      data-state={draft.state}
      data-proposed={pending ? "true" : undefined}
      role="status"
      aria-live="polite"
    >
      {draft.state === "drawing" && !pending ? <span className={styles.spinner} aria-hidden="true" /> : <span className={styles.dot} aria-hidden="true" />}
      {proposedStatus ?? status}
    </span>
  );

  const dim = draft.state === "drawing" && draft.svg ? "drawing" : error && draft.svg ? "last-good" : undefined;
  const scale = zoom === "fit" ? null : Number(zoom);
  const imgStyle: CSSProperties = {
    opacity: dim === "drawing" ? 0.6 : dim === "last-good" ? 0.4 : 1,
    ...(scale && natural ? { width: natural.w * scale, maxWidth: "none", maxHeight: "none" } : {}),
  };
  const note = error
    ? error.line !== null && !isOurSide(error.kind)
      ? `Fix line ${error.line} and it redraws on its own.`
      : ""
    : zoom === "fit"
      ? "Scaled to fit · the PDF uses the same drawing"
      : "";

  const viewTabs = plot && layout !== "tabs" && (
    <div className={styles.viewTabs} role="tablist" data-testid={testIds.tikzViews.tabs}>
      <button type="button" role="tab" aria-selected={leftView === "plot"} data-testid={testIds.tikzViews.plot} onClick={() => setLeftView("plot")}>
        Plot
      </button>
      <button type="button" role="tab" aria-selected={leftView === "source"} data-testid={testIds.tikzViews.source} onClick={() => setLeftView("source")}>
        Source
      </button>
    </div>
  );

  const plotPane = plot && leftView === "plot" && (
    <div className={styles.sourcePane} style={{ gridArea: layout === "tabs" ? "pane" : "source" }} hidden={layout === "tabs" && tab !== "source"}>
      {viewTabs}
      {!plot.table && (
        <p className={styles.plotNote} data-testid={testIds.plot.droppedNotice}>
          {plot.tableName === "a table" ? "The table" : plot.tableName} was removed. Restore it with Undo, or detach the plot.
        </p>
      )}
      {plot.problem && plot.table && <p className={styles.plotNote}>{plot.problem}</p>}
      {plot.dropped.length > 0 && (
        <p className={styles.plotNote} data-testid={testIds.plot.droppedNotice}>
          {plot.dropped.join(", ")} {plot.dropped.length === 1 ? "was" : "were"} removed from {plot.tableName}, so {plot.dropped.length === 1 ? "it's" : "they're"} no longer plotted.
        </p>
      )}
      {plot.table && (
        <PlotSettings table={plot.table} spec={plot.spec} onChange={changePlot} colours={colours} legendCorner={legendCornerOf(source)} />
      )}
    </div>
  );

  const sourcePane = (
    <div
      className={styles.sourcePane}
      data-testid={T.source}
      style={{ gridArea: layout === "tabs" ? "pane" : "source" }}
      hidden={(layout === "tabs" && tab !== "source") || (!!plot && leftView === "plot")}
    >
      {leftView === "source" && viewTabs}
      {plot && (
        <div className={styles.detachBar} data-testid={testIds.plot.detachBar}>
          <span>
            This source is written from <b>{plot.tableName}</b>. Read it or copy it freely. Editing it stops the plot following the table.
          </span>
          <button type="button" data-testid={testIds.plot.detachEdit} onClick={() => setDetachAsk({ apply: null })}>
            Detach and edit
          </button>
        </div>
      )}
      {pending && (
        <>
          <TikzDiffBar change={pending} current={source} onKeep={() => void keepChange()} onDiscard={discardChange} />
          <TikzDiffSource
            current={source}
            change={pending}
            highlight={highlight}
            onKeep={() => void keepChange()}
            onDiscard={discardChange}
            onType={(key) => void keepChange(key)}
          />
        </>
      )}
      <div className={styles.editorSlot} hidden={!!pending}>
        <TikzSourceEditor
          handle={editor}
          value={source}
          onChange={props.onSourceChange}
          colours={colours}
          error={inlineError}
          onDrawNow={() => draft.drawNow()}
          onDone={props.onDone}
          onLeave={leaveEditor}
          onEscArmed={setEscArmed}
          highlight={highlight}
          locked={!!plot}
          onLockedEdit={(apply) => setDetachAsk({ apply })}
        />
      </div>
      <div className={styles.hints}>
        {escArmed ? (
          <span>
            <b>Tab</b> leaves the editor · <b>Esc</b> again: Done
          </span>
        ) : (
          <>
            <span>Redraws {draft.redrawMs / 1000} s after you stop typing</span>
            <span>
              <b>⌘S</b> draw now
            </span>
            <span>
              <b>⌘/</b> comment
            </span>
            <span>
              <b>Esc</b> then Tab leaves the editor
            </span>
          </>
        )}
      </div>
    </div>
  );

  const drawingPane = (
    <div className={styles.drawingPane} data-testid={T.drawing} style={{ gridArea: layout === "tabs" ? "pane" : "drawing" }} hidden={layout === "tabs" && tab !== "drawing"}>
      <div className={styles.paneHead}>
        <span className={styles.paneTitle}>Drawing</span>
        <span className={styles.paneSub}>as it prints</span>
        <span className={styles.flex} />
        {pending && <TikzBeforeAfter after={showAfter} onChange={setShowAfter} />}
        <select className={styles.zoom} data-testid={T.zoom} value={zoom} aria-label="Zoom" onChange={(e) => setZoom(e.target.value)}>
          {ZOOMS.map((z) => (
            <option key={z.value} value={z.value}>
              {z.label}
            </option>
          ))}
        </select>
      </div>
      <div className={styles.paneBody} data-zoom={zoom}>
        {dim === "last-good" && <span className={styles.lastGoodLabel}>Last version that drew</span>}
        <div className={styles.paper}>
          {pending && showAfter ? (
            pending.svg ? (
              <img src={pending.svg} alt={caption || "Ask Lilia's drawing"} className={styles.drawingImg} data-testid={T.drawingImage} data-proposed="true" />
            ) : (
              <div className={askStyles.afterError} role="status">
                <b>Ask Lilia&apos;s version didn&apos;t draw{pending.error?.line != null ? ` (line ${pending.error.line})` : ""}.</b>{" "}
                {pending.error?.message ?? ""}
              </div>
            )
          ) : draft.svg ? (
            <img
              src={draft.svg}
              alt={caption || "TikZ drawing"}
              className={styles.drawingImg}
              data-testid={T.drawingImage}
              data-dim={dim}
              style={imgStyle}
              onLoad={(e) => setNatural({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
            />
          ) : (
            <div className={styles.noDrawing}>{draft.state === "drawing" ? "Drawing…" : "Nothing has drawn yet."}</div>
          )}
          {caption.trim() && <div className={styles.paperCaption}>{caption}</div>}
        </div>
        {error && (isOurSide(error.kind) || error.line === null) && (
          <div className={styles.errorPanel} data-testid={T.errorPanel} data-kind={error.kind} role="status">
            <span>{isOurSide(error.kind) ? ourSideWording(error.kind) : error.message}</span>
            {isOurSide(error.kind) && (
              <button type="button" data-testid={T.tryAgain} onClick={() => draft.drawNow()}>
                Try again
              </button>
            )}
          </div>
        )}
        {pending ? (
          <span className={styles.note}>{showAfter ? "After: Ask Lilia’s version" : "Before: your version"}</span>
        ) : (
          note && <span className={styles.note}>{note}</span>
        )}
      </div>
    </div>
  );

  const gridStyle: CSSProperties =
    layout === "side"
      ? { gridTemplateColumns: `minmax(0, ${split}fr) 6px minmax(0, ${1 - split}fr)`, gridTemplateAreas: '"source splitter drawing"' }
      : layout === "stacked"
        ? { gridTemplateRows: "40% minmax(0, 1fr)", gridTemplateAreas: '"drawing" "source"' }
        : { gridTemplateRows: "minmax(0, 1fr)", gridTemplateAreas: '"pane"' };

  return (
    <div ref={setRoot} className={styles.sheet} data-testid={T.root} data-layout={layout} data-fixed={props.fixed ? "true" : undefined} role="dialog" aria-label="TikZ source and drawing">
      <div className={styles.header}>
        <span className={styles.kind}>TikZ</span>
        <span className={styles.figureName}>
          Figure
          {props.label && <span className={styles.labelKey}>{props.label}</span>}
        </span>
        <input
          className={styles.caption}
          data-testid={T.caption}
          value={caption}
          placeholder="Caption (a caption numbers the figure)"
          aria-label="Caption"
          onFocus={() => (captionFocused.current = true)}
          onBlur={() => {
            captionFocused.current = false;
            props.onCaptionCommit();
          }}
          onChange={(e) => {
            setCaption(e.target.value);
            props.onCaptionChange(e.target.value);
          }}
        />
        {chip}
        <div className={styles.templatesWrap}>
          <button
            type="button"
            className={styles.textButton}
            data-testid={T.templates}
            aria-haspopup="dialog"
            aria-expanded={popover}
            onClick={() => setPopover((o) => !o)}
          >
            Templates
          </button>
          {popover && (
            <div className={styles.popover} data-testid={T.templatesPopover} role="dialog" aria-label="Templates">
              <TikzTemplates compact onPick={pickTemplate} />
            </div>
          )}
        </div>
        <button type="button" className={styles.done} data-testid={T.done} onClick={props.onDone}>
          Done
        </button>
      </div>

      {confirm && (
        <div className={styles.confirm} data-testid={T.replaceConfirm} role="alertdialog" aria-label="Replace this figure's source?">
          <span>
            <b>Replace this figure&apos;s source?</b> The current source stays in undo (⌘Z).
          </span>
          <button type="button" className={styles.textButton} data-testid={T.replaceCancel} onClick={() => setConfirm(null)}>
            Keep mine
          </button>
          <button type="button" className={styles.done} data-testid={T.replaceOk} onClick={() => void applyTemplate(confirm)}>
            Replace
          </button>
        </div>
      )}

      {notice && (
        <div className={styles.notice} data-testid={T.preambleNotice} role="status">
          <span>{notice.text}</span>
          {notice.lines.length > 0 && (
            <button type="button" className={styles.textButton} data-testid={T.preambleUndo} onClick={() => void undoPreamble()}>
              Undo
            </button>
          )}
        </div>
      )}

      {layout === "tabs" && !showGallery && (
        <div className={styles.tabs} role="tablist">
          {/* A linked plot, narrow: one row of tabs, Plot · Source · Drawing. */}
          {plot && (
            <button
              type="button"
              role="tab"
              aria-selected={tab === "source" && leftView === "plot"}
              data-testid={testIds.tikzViews.plot}
              className={styles.tab}
              onClick={() => {
                setTab("source");
                setLeftView("plot");
              }}
            >
              Plot
            </button>
          )}
          <button
            type="button"
            role="tab"
            aria-selected={tab === "source" && (!plot || leftView === "source")}
            data-testid={T.tabSource}
            className={styles.tab}
            onClick={() => {
              setTab("source");
              setLeftView("source");
            }}
          >
            Source
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "drawing"}
            data-testid={T.tabDrawing}
            className={styles.tab}
            onClick={() => setTab("drawing")}
          >
            Drawing
          </button>
        </div>
      )}

      {showGallery ? (
        <div className={styles.galleryWrap} data-testid={T.gallery}>
          <TikzTemplates onPick={pickTemplate} />
        </div>
      ) : (
        <div ref={gridRef} className={styles.grid} style={gridStyle}>
          {sourcePane}
          {plotPane}
          {detachAsk && plot && (
            <div className={styles.detachBackdrop}>
              <div className={styles.detachAsk} role="alertdialog" aria-modal="true" aria-labelledby="plot-detach-title" data-testid={testIds.plot.detachConfirm}>
                <h4 id="plot-detach-title">Stop following {plot.tableName}?</h4>
                <p>The plot becomes plain TikZ that you edit by hand. Changes to {plot.tableName} won&rsquo;t reach it any more.</p>
                <div className={styles.detachActions}>
                  <button
                    type="button"
                    data-testid={testIds.plot.detachConfirmKeep}
                    autoFocus
                    onClick={() => {
                      setDetachAsk(null);
                      requestAnimationFrame(() => editor.current?.focus());
                    }}
                  >
                    Keep following
                  </button>
                  <button type="button" className={styles.detachPrimary} data-testid={testIds.plot.detachConfirmDetach} onClick={() => detach(detachAsk.apply)}>
                    Detach
                  </button>
                </div>
              </div>
            </div>
          )}
          {layout === "side" && (
            <div
              className={styles.splitter}
              data-testid={T.splitter}
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize source and drawing"
              aria-valuemin={25}
              aria-valuemax={75}
              aria-valuenow={Math.round(split * 100)}
              tabIndex={0}
              style={{ gridArea: "splitter" }}
              onPointerDown={onSplitterDown}
              onPointerMove={onSplitterMove}
              onPointerUp={onSplitterUp}
              onKeyDown={onSplitterKey}
            >
              <span className={styles.grip} />
            </div>
          )}
          {drawingPane}
        </div>
      )}
    </div>
  );
}

